import { FC } from 'react';
import { Calendar, DollarSign, Star, TrendingUp, Clock, Users } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import type {
  TherapistDashboardStats,
  TherapistProfile,
} from '../../../types/therapist-dashboard.types';

interface TherapistStatsProps {
  stats: TherapistDashboardStats | undefined;
  profile: TherapistProfile | undefined;
  isLoading: boolean;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'purple';
  isLoading?: boolean;
}

const colorClasses = {
  primary: 'bg-primary-100 text-primary-600',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-yellow-100 text-yellow-600',
  error: 'bg-red-100 text-red-600',
  purple: 'bg-purple-100 text-purple-600',
};

function StatCard({ title, value, icon, trend, color = 'primary', isLoading }: StatCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className={cn('p-3 rounded-lg', colorClasses[color])}>{icon}</div>
      </div>

      <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-1">{value}</p>
      <p className="text-sm text-gray-600 dark:text-slate-400 mb-2">{title}</p>

      {trend && <p className="text-xs text-gray-500 dark:text-slate-400">{trend}</p>}
    </div>
  );
}

export const TherapistStats: FC<TherapistStatsProps> = ({ stats, profile, isLoading }) => {
  return (
    <div className="space-y-8">
      {/* Today's Overview */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4">Today's Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Today's Appointments"
            value={stats?.todayAppointments || 0}
            icon={<Calendar className="w-6 h-6" />}
            color="primary"
            isLoading={isLoading}
          />

          <StatCard
            title="Today's Earnings"
            value={formatCurrency(stats?.todayEarnings || 0)}
            icon={<DollarSign className="w-6 h-6" />}
            color="success"
            isLoading={isLoading}
          />

          <StatCard
            title="Average Rating"
            value={stats?.averageRating?.toFixed(1) || 'N/A'}
            icon={<Star className="w-6 h-6" />}
            trend={`${profile?.totalReviews || 0} reviews`}
            color="warning"
            isLoading={isLoading}
          />

          <StatCard
            title="Completion Rate"
            value={`${stats?.completionRate || 0}%`}
            icon={<TrendingUp className="w-6 h-6" />}
            color="purple"
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Weekly Stats */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4">This Week</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                <Calendar className="w-6 h-6" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-1">
              {isLoading ? '-' : stats?.weekAppointments || 0}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Appointments</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-1">
              {isLoading ? '-' : formatCurrency(stats?.weekEarnings || 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Earnings</p>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 text-purple-600 rounded-lg">
                <Clock className="w-6 h-6" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-1">
              {isLoading ? '-' : `${stats?.weekAppointments || 0}h`}
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">Hours Worked</p>
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4">This Month</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
                  {isLoading ? '-' : stats?.monthAppointments || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-400">Monthly Appointments</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900 dark:text-slate-100">
                  {isLoading ? '-' : formatCurrency(stats?.monthEarnings || 0)}
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-400">Monthly Earnings</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
